import React, { useEffect, useState, useRef } from 'react';
import { collection, getDocs, doc, updateDoc, query, orderBy, getDoc, where, runTransaction, deleteDoc } from 'firebase/firestore';
import { db, functions } from '../protected/firebase';
import { useAuth } from '../../../contexts/AuthContext';
import ReceiptPreview from '../../../components/common/ReceiptPreview';
import QRCode from 'react-qr-code';
import { FiSearch, FiFilter, FiArrowUp, FiArrowDown, FiEye, FiShare2, FiPrinter, FiXCircle } from 'react-icons/fi';
import { httpsCallable } from 'firebase/functions';

export default function History() {
  const { user, companyId } = useAuth();
  const [orders, setOrders] = useState([]);
  const [filterDate, setFilterDate] = useState('');
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('timestamp');
  const [sortAsc, setSortAsc] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [companySnap, setCompanySnap] = useState({});
  const [companyInfo, setCompanyInfo] = useState({});
  const receiptRef = useRef();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [voiding, setVoiding] = useState(false);
  const [voidTarget, setVoidTarget] = useState(null);
  const toNumber = (val) => {
    const num = Number(val);
    return Number.isFinite(num) ? num : 0;
  };
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrUrl, setQrUrl] = useState('');
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(false);

  const formatDate = (date) => {
    const d = new Date(date);
    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`;
  };
  const formatTime = (date) => {
    const d = new Date(date);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleGenerateQR = async (order) => {
    // Get the user's public ID first
    const userDoc = await getDoc(doc(db, 'companies', companyId));
    const publicId = userDoc.data()?.publicId;
    const domain = window.location.origin;
    const receiptId = order.id;
    const publicUrl = `${domain}/publicReceipts/${receiptId}?pid=${publicId}`;
    setQrUrl(publicUrl);
    setShowQRModal(true);
  };

  const voidReceipt = async () => {
    if (!voidTarget || !user || !companyId) return;
    setVoiding(true);

    try {
      const paymentStatus = (voidTarget.paymentStatus || '').toLowerCase();
      const paymentMethod = (voidTarget.paymentMethod || '').toLowerCase();
      const paymentProvider = (voidTarget.paymentProvider || '').toLowerCase();
      const needsRefund =
        paymentStatus === 'paid' && (paymentMethod.includes('stripe') || paymentProvider === 'stripe');
      if (needsRefund) {
        const refundPayment = httpsCallable(functions, 'refundOnlineOrderPayment');
        await refundPayment({ companyId, orderId: voidTarget.id });
      }

      const receiptRef = doc(db, 'companies', companyId, 'orders', voidTarget.id);
      await updateDoc(receiptRef, {
        paymentStatus: 'Void',
        voidReason,
        voidedAt: new Date().toISOString()
      }); 
      const userDoc = await getDoc(doc(db, 'companies', companyId));
      const publicId = userDoc.data()?.publicId;
      const publicRef = doc(db, 'publicUsers',publicId, 'receipts', voidTarget.id);
      await updateDoc(publicRef, {
        paymentStatus: 'Void',
        voidReason,
        voidedAt: new Date().toISOString()
      });

      const preRef = doc(db, 'companies', companyId, 'preorders', voidTarget.id);
      const preSnap = await getDoc(preRef);
      if (preSnap.exists()) {
        await deleteDoc(preRef);
      }

      // Restore inventory for each item (supports bundle + variant)
      const adjustStock = async (productId, variantId, qtyToRestore) => {
        if (!productId || !qtyToRestore) return;
        const productRef = doc(db, `companies/${companyId}/inventory/data/products`, productId);
        await runTransaction(db, async (transaction) => {
          const snap = await transaction.get(productRef);
          if (!snap.exists()) return;
          const data = snap.data();
          if (variantId) {
            const variantsArr = Array.isArray(data.variants) ? [...data.variants] : [];
            const idx = variantsArr.findIndex(v => v.id === variantId || v.variantId === variantId);
            if (idx === -1) return;
            const currentQty = toNumber(variantsArr[idx].qty);
            variantsArr[idx] = { ...variantsArr[idx], qty: currentQty + qtyToRestore };
            const totalQty = variantsArr.reduce((s, v) => s + toNumber(v.qty), 0);
            transaction.update(productRef, { variants: variantsArr, qty: totalQty });
          } else {
            const currentQty = toNumber(data.qty);
            transaction.update(productRef, { qty: currentQty + qtyToRestore });
          }
        });
      };

      for (const item of voidTarget.items) {
        if (!item.name) continue;

        // Handle bundle items
        if (item.items && Array.isArray(item.items)) {
          // This is a bundle - process each item in the bundle
          for (const bundleItem of item.items) {
            if (!bundleItem.quantity) continue;
            const restoreQty = toNumber(bundleItem.quantity) * toNumber(item.qty || 1);
            const variantId = bundleItem.variantId || bundleItem.variant_id || null;
            if (bundleItem.id) {
              await adjustStock(bundleItem.id, variantId, restoreQty);
            } else {
              // Fallback by name
              const productsRef = collection(db, `companies/${companyId}/inventory/data/products`);
              const q = query(productsRef, where('name', '==', bundleItem.name));
              const querySnap = await getDocs(q);
              for (const docSnap of querySnap.docs) {
                await adjustStock(docSnap.id, variantId, restoreQty);
              }
            }
          }
        } else {
          // Handle regular products
          if (!item.qty) continue;
          const variantId = item.variantId || item.variant_id || null;
          
          // First try to find by ID if available
          if (item.id) {
            try {
              await adjustStock(item.id, variantId, toNumber(item.qty));
              continue; // Skip to next item if found by ID
            } catch (error) {
              console.warn(`Error finding product by ID ${item.id}:`, error);
            }
          }
          
          // Fallback to finding by name if ID lookup fails or not available
          const productsRef = collection(db, `companies/${companyId}/inventory/data/products`);
          const q = query(productsRef, where('name', '==', item.name));
          const querySnap = await getDocs(q);
          for (const docSnap of querySnap.docs) {
            await adjustStock(docSnap.id, variantId, toNumber(item.qty));
          }
        }
      }

      alert('Receipt voided successfully.');
      setOrders(prev => prev.map(order => 
        order.id === voidTarget.id ? { ...order, paymentStatus: 'Void', voidReason } : order
      ));
      setReceipts(prev => prev.map(receipts => 
        receipts.id === voidTarget.id ? { ...receipts, paymentStatus: 'Void', voidReason } : receipts
      ));
      setShowVoidModal(false);
      setVoidTarget(null);
      setVoidReason('');
    } catch (error) {
      console.error('Error voiding receipt:', error);
      alert('Failed to void receipt.');
    }

    setVoiding(false);
  };

  useEffect(() => {
    setFilterDate(new Date().toISOString().split('T')[0]);
  }, []);

  const loadOrders = async () => {
    if (!user) return;
    try {
      const ordersRef = collection(db, 'companies', companyId, 'orders');
      const q = query(ordersRef, orderBy(sortField, sortAsc ? 'asc' : 'desc'));
      const querySnapshot = await getDocs(q);
      const ordersData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setOrders(ordersData);

      const profileDoc = await getDoc(doc(db, 'companies', companyId, 'companyProfile', 'info'));
      if (profileDoc.exists()) {
        setCompanySnap(profileDoc.data());
      }

      const companySettingsDoc = await getDoc(doc(db, 'companies', companyId, 'companySettings', 'info'));
      if (companySettingsDoc.exists()) {
        setCompanyInfo(companySettingsDoc.data());
      }
    } catch (error) {
      console.error('Error loading orders:', error);
    }
  };

  const handlePrint = (order) => {
    setSelectedOrder(order);
    setTimeout(() => {
      setTimeout(() => {
        printReceipt();
      }, 500);
    }, 0);
  };

  const printReceipt = () => {
    const printContent = receiptRef.current?.innerHTML;
    if (!printContent) {
      alert("Receipt not ready.");
      return;
    }

    const printWindow = window.open('', 'PRINT', 'width=300,height=600');
    const style = `
    <style>
    @media print {
      body {
        margin: 0 auto;
      }
    }
    body {
      margin: 0 auto;
      padding: 0 10px;
      font-family: monospace;
      font-size: 14px;
    }
    .receipt {
      width: 280px;
      padding: 0 10px;
      margin: 0 auto;
      font-size: 14px;
      font-family: monospace;
    }
    .center {
      text-align: center;
    }
    .bold {
      font-weight: bold;
    }
    .line {
      border-top: 1px dashed #000;
      margin: 5px 0;
    }
    .flex {
      display: flex;
      justify-content: space-between;
    }
    img {
      max-height: 40px;
      margin: 0 auto 10px;
      display: block;
    }
    .flex span:first-child {
      text-align: left;
      flex: 1;
    }
    .flex span:last-child {
      text-align: right;
      min-width: 60px;
    }
    .text-center {
      text-align: center;
    }
  </style>
`;

    printWindow.document.write(`
      <html>
        <head><title>Receipt</title>${style}</head>
        <body><div class="receipt">${printContent}</div>
          <script>
            window.onload = function () {
              window.focus();
              window.print();
              window.onafterprint = function () { window.close(); };
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const filtered = orders.filter(order => {
    const matchesSearch = String(order.receiptNumber).includes(search);
    const matchesDate = filterDate ? 
      new Date(order.timestamp).toISOString().split('T')[0] === filterDate : 
      true;
    return matchesSearch && matchesDate;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  const paginated = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([loadOrders()]);
      setLoading(false);
    };
    loadAll();
  }, [user, sortField, sortAsc, companyId]);

  if (loading) {
    return (
      <div className="w-full flex flex-col justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-purple-500 mb-4"></div>
        <span className="text-white">Loading information...</span>
      </div>
    );
  }

  return (
    <div className="text-white px-4 py-6 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-100">Receipt History</h1>
        <p className="mt-1 text-sm text-gray-400">View and manage your sales history</p>
      </div>
      
      {/* Filters */}
      <div className="bg-gray-800 rounded-lg p-4 mb-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-300">Date</label>
            <input 
              type="date" 
              value={filterDate} 
              onChange={(e) => setFilterDate(e.target.value)} 
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-300">Search Receipts</label>
            <div className="relative">
              <input 
                type="text" 
                placeholder="Receipt number..." 
                value={search} 
                onChange={(e) => setSearch(e.target.value)} 
                className="w-full pl-10 pr-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <FiSearch className="absolute left-3 top-2.5 text-gray-400" />
            </div>
          </div>
          
          <div className="flex items-end">
            <button 
              onClick={() => setSortAsc(!sortAsc)}
              className="w-full px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-md flex items-center justify-center gap-2 transition-colors"
            >
              <FiFilter className="text-purple-400" />
              <span>Sort by {sortField === 'timestamp' ? 'Date' : 'Receipt No'}</span>
              {sortAsc ? <FiArrowUp /> : <FiArrowDown />}
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-gray-800 rounded-lg overflow-hidden shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-700">
            <thead>
              <tr className="text-left text-gray-400">
                <th className="px-4 py-2">Date</th>
                <th className="px-4 py-2">Receipt #</th>
                <th className="px-4 py-2">Total</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginated.map(order => (
                <tr key={order.id} className="border-t border-gray-700">
                  <td className="px-4 py-2">{formatDate(order.timestamp)} {formatTime(order.timestamp)}</td>
                  <td className="px-4 py-2">#{order.receiptNumber}</td>
                  <td className="px-4 py-2">RM {order.total.toFixed(2)}</td>
                  <td className="px-4 py-2">{order.paymentStatus || 'Paid'}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center space-x-3">
                      <button 
                        onClick={() => setSelectedOrder(order)} 
                        className="p-2.5 text-gray-200 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors group"
                        title="Preview"
                      >
                        <FiEye className="w-5 h-5" />
                      </button>
                      
                      <button
                        onClick={() => handleGenerateQR(order)}
                        className="p-2.5 text-blue-400 bg-blue-900/30 rounded-lg hover:bg-blue-800/50 transition-colors group"
                        title="Share"
                      >
                        <FiShare2 className="w-5 h-5" />
                      </button>
                      
                      <button 
                        onClick={() => handlePrint(order)} 
                        disabled={order.paymentStatus === 'Void'}
                        className={`p-2.5 rounded-lg transition-colors ${order.paymentStatus === 'Void' 
                          ? 'text-gray-500 bg-gray-800 cursor-not-allowed' 
                          : 'text-green-400 bg-green-900/30 hover:bg-green-800/50'}`}
                        title="Print"
                      >
                        <FiPrinter className="w-5 h-5" />
                      </button>
                      
                      {order.paymentStatus !== 'Void' && (
                        <button
                          onClick={() => {
                            setShowVoidModal(true);
                            setVoidTarget(order);
                          }}
                          className="p-2.5 text-red-400 bg-red-900/30 rounded-lg hover:bg-red-800/50 transition-colors group"
                          title="Void"
                        >
                          <FiXCircle className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {paginated.length === 0 && (
                <tr><td className="px-4 py-4 text-gray-400" colSpan={5}>No receipts found.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex justify-center flex-wrap mt-4 space-x-2 gap-y-2">
          <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} className="px-3 py-1 bg-gray-600 rounded disabled:opacity-50">Prev</button>
          {[...Array(totalPages)].map((_, idx) => (
            <button key={idx} onClick={() => setCurrentPage(idx + 1)} className={`px-3 py-1 rounded ${currentPage === idx + 1 ? 'bg-purple-600 text-white' : 'bg-gray-700 text-gray-300'}`}>{idx + 1}</button>
          ))}
          <button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages} className="px-3 py-1 bg-gray-600 rounded disabled:opacity-50">Next</button>
        </div>
      </div>

      {selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 overflow-auto">
          <div className="bg-white p-6 rounded-lg text-black w-[350px] relative max-h-[90vh] overflow-y-auto">
            <div ref={receiptRef}>
              {(() => {
                const mergedCompanyInfo = {
                  ...companyInfo,
                  thankYouNote: selectedOrder.thankYouNote || companyInfo.thankYouNote,
                  receiptFooter: selectedOrder.footer || companyInfo.receiptFooter,
                };
                return (
              <ReceiptPreview
                receipt={selectedOrder}
                companySnap={companySnap}
                companyInfo={mergedCompanyInfo}
                paymentMethod={selectedOrder.paymentMethod}
                taxEnabled={companyInfo.taxEnabled}
                taxRate={companyInfo.taxRate}
              />
                );
              })()}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setSelectedOrder(null)} className="px-4 py-2 bg-purple-500 rounded text-white">Close</button>
            </div>
          </div>
        </div>
      )}

      {showQRModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
          <div className="bg-white text-black p-6 rounded-lg w-[320px] text-center">
            <h3 className="text-xl font-semibold mb-3">Scan to View Receipt</h3>
            <QRCode value={qrUrl} size={200} className="mx-auto" />
            <p className="text-xs mt-4 break-all">{qrUrl}</p>
            <div className="mt-4 flex justify-center gap-2">
              <button onClick={() => {
                navigator.clipboard.writeText(qrUrl);
                alert('URL copied to clipboard');
              }}
                className="px-3 py-1 bg-blue-600 text-white rounded"
              >
                Copy URL
              </button>
              <button onClick={() => setShowQRModal(false)} className="px-3 py-1 bg-red-500 text-white rounded">Close</button>
            </div>
          </div>
        </div>
      )}

      {showVoidModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-[#1a1a1a] rounded-lg shadow-xl w-full max-w-md p-6 text-white">
            <h3 className="text-xl font-semibold mb-2">Void Receipt</h3>
            <p className="text-sm text-gray-300 mb-4">Please provide a reason for voiding this receipt:</p>

            <textarea
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              className="w-full bg-[#111] border border-gray-700 rounded-md px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-600"
              rows={3}
              placeholder="Enter reason..."
            ></textarea>

            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setShowVoidModal(false)}
                className="px-4 py-2 text-sm rounded-md bg-gray-700 hover:bg-gray-600 text-white"
              >
                Cancel
              </button>
              <button
                onClick={voidReceipt}
                disabled={voiding || !voidReason}
                className="px-4 py-2 text-sm rounded-md bg-red-600 hover:bg-red-500 text-white disabled:opacity-50"
              >
                {voiding ? 'Voiding...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
